import { useCallback, useEffect, useState } from 'react';
import {
  getCollectionInquiries,
  isCollectionInquiryInboxUnavailable,
  readCollectionInquiries,
} from '../services/collectionInquiryService';

const EMPTY_STATS = { total: 0, unseenTotal: 0, byItem: {}, canViewLeads: null };

/** `can_view_leads` de la raíz del GET: true / false, o null si no vino (API pre-L2). */
function readCanViewLeads(res) {
  return typeof res?.can_view_leads === 'boolean' ? res.can_view_leads : null;
}

/**
 * Stats de la bandeja para manage. GET `/getcollectioninquiries` (no
 * `getcadproject`). Visitante → 404 = vacío. El mismo GET trae
 * `can_view_leads` (decide botón “Leads y consultas” vs badge de consultas).
 *
 * @param {{ listId?: string, projectId?: string, enabled?: boolean }} [query]
 */
export function useCollectionInquiryStats(query = {}) {
  const listId = String(query.listId || '').trim();
  const projectId = String(query.projectId || '').trim();
  const enabled = query.enabled !== false && Boolean(listId || projectId);
  const [stats, setStats] = useState(EMPTY_STATS);

  const apply = useCallback((next = {}) => {
    setStats((prev) => {
      const totalRaw = next.inquiryTotal ?? next.inquiry_total;
      const total = Number(totalRaw);
      const unseenRaw = next.unseenTotal ?? next.unseen_total;
      const unseenTotal = Number(unseenRaw);
      const byItem = next.byItem || next.by_item;
      return {
        total: Number.isFinite(total) ? Math.max(0, Math.trunc(total)) : prev.total,
        unseenTotal: Number.isFinite(unseenTotal)
          ? Math.max(0, Math.trunc(unseenTotal))
          : prev.unseenTotal,
        byItem: byItem && typeof byItem === 'object' && !Array.isArray(byItem)
          ? byItem
          : prev.byItem,
        canViewLeads: prev.canViewLeads,
      };
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStats(EMPTY_STATS);
      return undefined;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const res = await getCollectionInquiries(
          {
            ...(listId ? { listId } : { projectId }),
            limit: 1,
          },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (isCollectionInquiryInboxUnavailable(res._httpStatus) || !res._httpOk) {
          setStats(EMPTY_STATS);
          return;
        }
        const parsed = readCollectionInquiries(res);
        setStats({
          total: parsed.inquiry_total,
          unseenTotal: parsed.unseen_total,
          byItem: parsed.by_item,
          canViewLeads: readCanViewLeads(res),
        });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Consultas: GET stats falló', err);
        if (!controller.signal.aborted) setStats(EMPTY_STATS);
      }
    })();
    return () => controller.abort();
  }, [enabled, listId, projectId]);

  return {
    total: stats.total,
    unseenTotal: stats.unseenTotal,
    byItem: stats.byItem,
    /** Leads (F1): `false` = editor solo de esta lista → el detalle muestra Consultas. */
    canViewLeads: stats.canViewLeads,
    apply,
  };
}
